import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import { generateFurnitureEditing } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import { setGeneratedImageDragData } from '../../utils/dragDropUtils';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { downloadImage } from '../../utils/downloadUtils';
import { checkSaveDesignLimit } from '../../utils/planLimits';

const FurnitureEditing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [referenceImage1, setReferenceImage1] = useState([]);
  const [referenceImage2, setReferenceImage2] = useState([]);
  const [referenceImage3, setReferenceImage3] = useState([]);
  const [promptText, setPromptText] = useState('');
  const [selectedEditOption, setSelectedEditOption] = useState(null);
  const [isolateBackground, setIsolateBackground] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);
  const [generationError, setGenerationError] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);
  const [isDragging3, setIsDragging3] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [highlightButton, setHighlightButton] = useState(null);

  // Virtual Tool Guide
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const totalGuideSteps = 10;

  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();
  const promptRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const textureButtonRef = useRef(null);
  const textureButtonMobileRef = useRef(null);
  const removalButtonRef = useRef(null);
  const removalButtonMobileRef = useRef(null);
  const realismButtonRef = useRef(null);
  const realismButtonMobileRef = useRef(null);

  // Guide refs — desktop
  const editModeDesktopRef = useRef(null);
  const assetLibraryFurnitureDesktopRef = useRef(null);
  const resultFurnitureDesktopRef = useRef(null);
  const upload1DesktopRef = useRef(null);
  const upload2DesktopRef = useRef(null);
  const modelFurnitureDesktopRef = useRef(null);
  const promptFurnitureDesktopRef = useRef(null);
  const generateFurnitureDesktopRef = useRef(null);
  // Guide refs — mobile
  const editModeMobileRef = useRef(null);
  const assetLibraryFurnitureMobileRef = useRef(null);
  const resultFurnitureMobileRef = useRef(null);
  const upload1MobileRef = useRef(null);
  const upload2MobileRef = useRef(null);
  const modelFurnitureMobileRef = useRef(null);
  const promptFurnitureMobileRef = useRef(null);
  const generateFurnitureMobileRef = useRef(null);

  // Auto-select mode when navigated from dashboard with highlight param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlight = params.get('highlight');
    if (highlight === 'texture' || highlight === 'removal' || highlight === 'realism') {
      setSelectedEditOption(highlight);
      setHighlightButton(highlight);
      const refMap = {
        texture: { desktop: textureButtonRef, mobile: textureButtonMobileRef },
        removal: { desktop: removalButtonRef, mobile: removalButtonMobileRef },
        realism: { desktop: realismButtonRef, mobile: realismButtonMobileRef },
      };
      setTimeout(() => {
        const ref = window.innerWidth >= 1024 ? refMap[highlight].desktop : refMap[highlight].mobile;
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      const timer = setTimeout(() => setHighlightButton(null), 1600);
      navigate('/furniture-editing', { replace: true });
      return () => clearTimeout(timer);
    }
  }, []);

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setGeneratedImage(designs[0].imageUrl);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          const restoredPrompt = metadata.additionalParams?.prompt || designs[0].prompt;
          if (restoredPrompt) setPromptText(restoredPrompt);
          const restoredEditOption = metadata.additionalParams?.selectedEditOption || metadata.style;
          if (restoredEditOption) setSelectedEditOption(restoredEditOption);
          if (metadata.additionalParams?.isolateBackground != null) setIsolateBackground(metadata.additionalParams.isolateBackground);
        }
      }
    };
    loadProjectData();
  }, []);

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    setGuideVisible(false);
    const ref = window.innerWidth >= 768 ? editModeDesktopRef.current : editModeMobileRef.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }, 500);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
    }
  };

  const nextGuideStep = () => {
    if (guideAnimating) return;
    if (guideStep >= totalGuideSteps) { closeGuide(); return; }
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = [2, 3, 4, 5, 6, 7, 8, 9, 10].includes(nextStep);
    if (needsScroll) {
      const desktopRefs = [editModeDesktopRef, assetLibraryFurnitureDesktopRef, upload1DesktopRef, upload2DesktopRef, modelFurnitureDesktopRef, aspectRatioRef, resolutionRef, promptFurnitureDesktopRef, generateFurnitureDesktopRef, resultFurnitureDesktopRef];
      const mobileRefs = [editModeMobileRef, assetLibraryFurnitureMobileRef, upload1MobileRef, upload2MobileRef, modelFurnitureMobileRef, aspectRatioMobileRef, resolutionMobileRef, promptFurnitureMobileRef, generateFurnitureMobileRef, resultFurnitureMobileRef];
      const ref = window.innerWidth >= 768 ? desktopRefs[nextStep - 1]?.current : mobileRefs[nextStep - 1]?.current;
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
        }, 400);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
      setGuideAnimating(false);
    }, 600);
  };

  // Models constant removed - using shared ModelSelectionDropdown component


  const totalUploads = referenceImage1.length + referenceImage2.length + referenceImage3.length;

  // Get image dimensions based on selected size
  const getImageDimensions = () => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[selectedResolution]) {
      return ratioMap[selectedResolution];
    }
    const defaultDimensions = {
      Auto: { width: 1024, height: 1024 },
      "1:1": { width: 2048, height: 2048 },
      "4:3": { width: 2048, height: 1536 },
      "3:2": { width: 2048, height: 1365 },
      "16:9": { width: 2048, height: 1152 },
      "21:9": { width: 2048, height: 878 },
      "5:4": { width: 2048, height: 1638 },
      "2:3": { width: 1365, height: 2048 },
      "9:16": { width: 1152, height: 2048 },
      "3:4": { width: 1536, height: 2048 },
      "4:5": { width: 1638, height: 2048 },
    };
    return defaultDimensions[selectedImageSize] || { width: 1024, height: 1024 };
  };

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize] || resolutionMap['Auto'];
    return ratioMap ? ratioMap[resKey] || { width: 1024, height: 1024 } : { width: 1024, height: 1024 };
  };

  const handleReferenceImage1Upload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = referenceImage1.length + referenceImage2.length;
    const remaining = Math.max(0, 6 - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setReferenceImage1((prev) => [...prev, ...urls]);
  };

  const handleReferenceImage2Upload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = referenceImage1.length + referenceImage2.length;
    const remaining = Math.max(0, 6 - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setReferenceImage2((prev) => [...prev, ...urls]);
  };

  // Drag and drop handlers for first upload zone
  const handleDragOver1 = (e) => {
    e?.preventDefault();
    setIsDragging1(true);
  };

  const handleDragLeave1 = (e) => {
    e?.preventDefault();
    setIsDragging1(false);
  };

  const handleDrop1 = async (e) => {
    e?.preventDefault();
    setIsDragging1(false);

    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      setReferenceImage1((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }

    const files = Array.from(e?.dataTransfer?.files || []);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    if (imageFiles?.length > 0) {
      const imageUrls = (await Promise.all(imageFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
      setReferenceImage1([...referenceImage1, ...imageUrls]);
    }
  };

  // Drag and drop handlers for second upload zone
  const handleDragOver2 = (e) => {
    e?.preventDefault();
    setIsDragging2(true);
  };

  const handleDragLeave2 = (e) => {
    e?.preventDefault();
    setIsDragging2(false);
  };

  const handleDrop2 = async (e) => {
    e?.preventDefault();
    setIsDragging2(false);

    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      setReferenceImage2((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }

    const files = Array.from(e?.dataTransfer?.files || []);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    if (imageFiles?.length > 0) {
      const imageUrls = (await Promise.all(imageFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
      setReferenceImage2([...referenceImage2, ...imageUrls]);
    }
  };

  const handleRemoveReferenceImage1 = (idx) => {
    setReferenceImage1((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveReferenceImage2 = (idx) => {
    setReferenceImage2((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReferenceImage3Upload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = referenceImage1.length + referenceImage2.length + referenceImage3.length;
    const remaining = Math.max(0, 6 - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setReferenceImage3((prev) => [...prev, ...urls]);
  };

  const handleDragOver3 = (e) => {
    e?.preventDefault();
    setIsDragging3(true);
  };

  const handleDragLeave3 = (e) => {
    e?.preventDefault();
    setIsDragging3(false);
  };

  const handleDrop3 = async (e) => {
    e?.preventDefault();
    setIsDragging3(false);

    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      setReferenceImage3((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }

    const files = Array.from(e?.dataTransfer?.files || []);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    if (imageFiles?.length > 0) {
      const imageUrls = (await Promise.all(imageFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
      setReferenceImage3([...referenceImage3, ...imageUrls]);
    }
  };

  const handleRemoveReferenceImage3 = (idx) => {
    setReferenceImage3((prev) => prev.filter((_, i) => i !== idx));
  };

  const objectTip = 'Tip: screenshot of object from the main image';

  // Upload window config per editing mode
  const getUploadConfig = () => {
    switch (selectedEditOption) {
      case 'material':
        return { label1: 'Upload Object Image', tip1: objectTip, label2: 'Upload Material Image', tip2: null, showSecond: true, showThird: false };
      case 'realism':
        return { label1: 'Upload Object Image', tip1: objectTip, label2: null, tip2: null, showSecond: false, showThird: false };
      case 'placement':
        return { label1: 'Upload Room Image', tip1: null, label2: 'Upload Object Image', tip2: objectTip, showSecond: true, showThird: false };
      case 'replacement':
        return { label1: 'Upload Room Image', tip1: null, label2: 'Upload Current Object', tip2: 'The object currently in the room to be replaced', label3: 'Upload New Object', tip3: 'The new object to replace with', showSecond: true, showThird: true };
      case 'movement':
        return { label1: 'Upload Room Image', tip1: null, label2: 'Upload Object Image', tip2: objectTip, showSecond: true, showThird: false };
      case 'removal':
        return { label1: 'Upload Room Image', tip1: null, label2: 'Upload Object Image', tip2: objectTip, showSecond: true, showThird: false };
      case 'isolate':
        return { label1: 'Upload Object Image', tip1: objectTip, label2: null, tip2: null, showSecond: false, showThird: false };
      case 'texture':
        return { label1: 'Image Containing the Material', tip1: null, label2: null, tip2: null, showSecond: false, showThird: false };
      default:
        return { label1: 'Upload Reference Image 1', tip1: null, label2: 'Upload Reference Image 2', tip2: null, showSecond: true, showThird: false };
    }
  };

  const uploadConfig = getUploadConfig();

  const handleEditOptionClick = (option) => {
    setSelectedEditOption(option);
    if (option !== 'isolate') {
      setIsolateBackground(null);
    }
    // Clear images when switching modes
    setReferenceImage1([]);
    setReferenceImage2([]);
    setReferenceImage3([]);
  };

  const handleIsolateBackgroundClick = (bg) => {
    setIsolateBackground(bg);
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  // Close aspect ratio dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktopAR = aspectRatioRef.current && aspectRatioRef.current.contains(e.target);
      const inMobileAR = aspectRatioMobileRef.current && aspectRatioMobileRef.current.contains(e.target);
      if (!inDesktopAR && !inMobileAR) {
        setIsAspectRatioOpen(false);
      }
      const inDesktopRes = resolutionRef.current && resolutionRef.current.contains(e.target);
      const inMobileRes = resolutionMobileRef.current && resolutionMobileRef.current.contains(e.target);
      if (!inDesktopRes && !inMobileRes) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = 'auto';
      promptRef.current.style.height = promptRef?.current?.scrollHeight + 'px';
    }
  }, [promptText]);



  // Build the full system prompt based on selected editing mode
  const buildSystemPrompt = () => {
    const dimensions = getImageDimensions();

    let modeSpecificPrompt = '';

    switch (selectedEditOption) {
      case 'material':
        modeSpecificPrompt = `Mode 1 — Material Change (selectedEditOption = 'material')
Purpose: Replace the material/textile on a furniture piece or object with a new material provided by the user.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Object Image" — the furniture piece or object whose material must be changed.
referenceImage2 (REQUIRED) — "Upload Material Image" — the textile, fabric, or material texture to apply onto the object.
Task: Apply the material from referenceImage2 onto the object in referenceImage1. The object's exact shape, form, proportions, silhouette, and structural details must remain identical — only the surface material changes. The material must wrap naturally around the object's geometry, following its curves, edges, folds, and contours realistically. Preserve the object's lighting environment and shadow behavior. The result must look like the same object was manufactured with the new material, not like a texture was pasted flat on top.
High-quality keywords: material replacement, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera, accurate material wrapping and surface reflection`;
        break;

      case 'realism':
        modeSpecificPrompt = `Mode 2 — Realism & Quality Enhancement (selectedEditOption = 'realism')
Purpose: Upscale and enhance the visual quality and realism of a low-quality object image or room scene.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Object Image" — the low-quality image of a prop or room to enhance.
referenceImage2 — not shown, not used in this mode.
Task: Make the image more realistic. Enhance the 3D depth and spatial feel. Maintain a high level of consistency with the original — do not change any object position, room composition, color palette, or lighting. Everything must remain the same — only enhance hyperrealism, surface texture detail, material fidelity, lighting quality, and spatial depth. Slight creative enhancement is allowed to elevate quality, but never alter the composition or introduce new elements. The result must look like the same scene photographed with a professional camera and better lighting equipment.
High-quality keywords: realism enhancement, hyper realism, photorealism, enhanced 3D depth, shot on professional camera, maintain original composition`;
        break;

      case 'movement':
        modeSpecificPrompt = `Mode 3 — Object Movement (selectedEditOption = 'movement')
Purpose: Move a specific object to a new position within the room scene.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Room Image" — the full room scene containing the object.
referenceImage2 (REQUIRED) — "Upload Object Image" — the specific object the user wants to move.
Task: Identify the object from referenceImage2 within the room scene in referenceImage1. Move it to the new position as described by the user in promptText${promptText ? `: "${promptText}"` : ''}. The user will state direction, distance, or placement instructions. The moved object must maintain its exact visual appearance — same material, same scale, same lighting adaptation. The vacated space must be filled naturally. All other objects in the room must remain completely untouched. The result must look physically plausible.
High-quality keywords: object repositioning, hyper realism, photorealism, natural spatial integration, consistent lighting and shadows`;
        break;

      case 'removal':
        modeSpecificPrompt = `Mode 4 — Object Removal (selectedEditOption = 'removal')
Purpose: Completely remove a specific object from a room scene.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Room Image" — the full room scene containing the object.
referenceImage2 (REQUIRED) — "Upload Object Image" — the specific object the user wants removed.
Task: Identify the object from referenceImage2 within the room scene in referenceImage1. Remove it entirely. The space previously occupied by the object must be filled naturally and seamlessly, whatever surface was behind or beneath the object. All other objects in the room must remain completely untouched in their exact positions, materials, and lighting. The result must look like the object was never there.
High-quality keywords: object removal, seamless inpainting, hyper realism, photorealism, natural surface reconstruction`;
        break;

      case 'placement':
        modeSpecificPrompt = `Mode 3 — Object & Material Placement (selectedEditOption = 'placement')
Purpose: Place a furniture piece, object, or surface material (carpet, upholstery, rug) naturally and cohesively into an existing room scene, integrating it as if it was always part of the original interior.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Room Image" — the main interior scene or setup into which the object or material must be placed. This is the spatial context — its camera angle, perspective, lighting direction, ambient colour temperature, and architectural environment define how the placed element must behave visually.
referenceImage2 (REQUIRED) — "Upload Object Image" — the furniture piece, object, or surface material to be placed into the scene. This can be a standalone furniture item (chair, table, lamp, decorative piece) or a surface material (carpet texture, rug, upholstery fabric, floor finish).
Task: Before placing, analyse both images. From referenceImage1, identify the camera angle, lens perspective, vanishing points, lighting direction, ambient colour temperature, shadow behaviour, floor plane geometry, and spatial depth. From referenceImage2, identify what is being placed — determine if it is a furniture object (has physical form and volume, occupies floor or surface space) or a surface material (flat textile or finish that must conform to and cover a surface area such as floor, wall, or upholstery).
For furniture objects: place the item into the scene at a position that is functionally logical and compositionally considered — according to the object's purpose in the human world and according to the user's intent stated in promptText${promptText ? `: "${promptText}"` : ''}. The placed object must adopt the scene's existing lighting direction, cast accurate shadows onto surrounding surfaces, receive ambient reflections consistent with the environment, and sit on the correct spatial plane (floor, table surface, shelf) with proper contact shadows. The object must be scaled proportionally to the room's existing elements. The perspective foreshortening of the placed object must match the scene's camera angle and vanishing points precisely — mismatched perspective is the primary cause of unconvincing placement.
For surface materials (carpets, rugs, upholstery, floor finishes): apply the material onto the appropriate surface within the scene, following the surface's geometry, perspective, and spatial recession. The material must conform to the floor plane or furniture surface naturally — extending from foreground to background with correct perspective foreshortening, following the room's spatial depth. Preserve the material's exact pattern, texture, colour palette, and tactile quality from the reference. The material must integrate seamlessly with the existing environment — receiving ambient lighting reflections, casting soft contact shadows at edges, and blending naturally with surrounding floor or surface transitions. The texture must appear with ultra-realistic detail: surface depth, fibre quality, weave structure, and tactile dimension must be clearly rendered, not flat or digitally pasted.
For both types: the original room scene must be preserved precisely — same camera angle, same perspective, same lighting, same architectural details, same existing furniture positions. The only change is the addition of the placed element. The result must look like a single unified photograph where the placed element was always part of the scene.
High-quality keywords: natural object placement, seamless material integration, hyper realism, photorealism, perspective-accurate placement, matched lighting and shadows, tactile surface detail, unified composition, shot on professional camera`;
        break;

      case 'replacement':
        modeSpecificPrompt = `Mode 4 — Object & Material Replacement (selectedEditOption = 'replacement')
Purpose: Replace a specific existing object or surface material within a room scene with a new one provided by the user, maintaining the original scene's integrity and naturally integrating the replacement element.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Room Image" — the main interior scene containing the object or material to be replaced. This is the spatial context — its camera angle, perspective, lighting direction, ambient colour temperature, and architectural environment must be preserved precisely.
referenceImage2 (REQUIRED) — "Upload Current Object" — the specific object or material currently in the scene that the user wants replaced. This identifies the target element — its position, scale, and spatial footprint within the room define where and how the replacement will be integrated.
referenceImage3 (REQUIRED) — "Upload New Object" — the new object or material that must replace the current one. This is the replacement element whose exact form, material finish, colour, pattern, and character must be preserved faithfully in the final composition.
Task: Before generating, analyse all three images. From referenceImage1, identify the full room environment: camera angle, lens perspective, vanishing points, lighting direction, ambient colour temperature, shadow behaviour, spatial depth, and the position of all existing elements. From referenceImage2, identify the target element to be replaced — locate it precisely within the room scene, noting its exact position, scale relative to surrounding elements, the surface it occupies or sits on, and the spatial footprint it fills. From referenceImage3, identify the replacement element — determine if it is a furniture object (has physical form and volume) or a surface material (flat textile or finish that conforms to a surface).
For furniture object replacement: remove the current object (referenceImage2) from the scene and place the new object (referenceImage3) in the same spatial position. The new object must adopt the scene's existing lighting direction, cast accurate shadows, receive ambient reflections consistent with the environment, and sit on the correct spatial plane with proper contact shadows. Scale the new object proportionally to the room's existing elements — it does not need to be the exact same size as the old object, but it must be dimensionally plausible for its type. The space must be seamlessly reconstructed where the old object was removed if the new object has a different footprint — floor, wall, or surrounding surfaces must be filled naturally. All other elements in the room must remain completely untouched.
For surface material replacement (carpets, rugs, upholstery, wall finishes, floor finishes): identify the surface area currently occupied by the material in referenceImage2 within the room scene. Replace it with the material from referenceImage3, conforming to the exact same surface geometry, perspective foreshortening, and spatial recession as the original. The new material must follow the surface plane naturally from foreground to background, aligned with the room's spatial depth and floor or wall geometry. Preserve the new material's exact pattern, texture, colour palette, and tactile quality from the reference. The replacement must integrate seamlessly with the existing environment — receiving ambient lighting reflections, casting soft contact shadows at transitions, and blending naturally with surrounding surface edges. The texture must appear with ultra-realistic detail: surface depth, fibre quality, weave structure, and tactile dimension clearly rendered — not flat or digitally pasted.
For both types: the original room scene must be preserved precisely — same camera angle, same perspective, same lighting, same architectural details, same positions of all elements except the replaced one. The result must look like a single unified photograph where the replacement element was always part of the scene — a natural, cohesive composition, not a composited layer.
High-quality keywords: natural object replacement, seamless material swap, hyper realism, photorealism, perspective-accurate integration, matched lighting and shadows, tactile surface detail, unified composition, shot on professional camera`;
        break;

      case 'texture':
        modeSpecificPrompt = `Mode 5 — Texture Extraction (selectedEditOption = 'texture')
Purpose: Extract a material texture from an image where it appears on a surface (furniture, floor, wall) and generate a clean, isolated, full-frame texture swatch.
Required inputs:
referenceImage1 (REQUIRED) — "Image Containing the Material" — the image showing the texture applied on an object or surface.
referenceImage2 — not shown, not used in this mode.
Task: Identify the target material/texture in the uploaded image${promptText ? ` (specifically: ${promptText})` : ''}. Extract it and generate a clean, vertical, macro-view texture image where only the pure material fills the entire frame edge to edge. Remove all context — no furniture, no room, no objects, no edges, no borders. The texture must be presented as a seamless, tileable-looking material swatch at full resolution, as if photographed straight-on in a material library. Preserve the exact color, pattern scale, grain direction, and surface quality of the original material.
High-quality keywords: texture extraction, material swatch, macro texture photography, seamless material, full-frame texture, vertical orientation`;
        break;

      case 'isolate':
        modeSpecificPrompt = `Mode 6 — Isolate on Background (selectedEditOption = 'isolate')
Purpose: Isolate a specific object from a cluttered scene and place it on a clean background.
Required inputs:
referenceImage1 (REQUIRED) — "Upload Object Image" — the image containing the object in its current cluttered environment.
referenceImage2 — not shown, not used in this mode.
isolateBackground (REQUIRED) — one of: 'white', 'black', 'auto'. Defines the background color for the isolated object.
Task: Identify the target object${promptText ? ` (specifically: ${promptText})` : ' the user specifies in promptText'}. Cleanly separate it from its surrounding environment. Place it on a solid background as defined by isolateBackground:
${isolateBackground === 'white' ? "'white': pure white (#FFFFFF) background" :
            isolateBackground === 'black' ? "'black': pure black (#000000) background" :
              "'auto': AI selects the background color that best contrasts with and complements the object"}
The isolated object must retain its exact visual appearance — same materials, colors, textures, lighting, and proportions. The edges must be clean and precise with no residual background fragments. The result must look like a professional product photography cutout.
High-quality keywords: object isolation, product photography, clean cutout, studio background, precise edge separation`;
        break;
    }

    return `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Flash Image Preview image generation model for furniture and materials editing visualization.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.
Intro: /furniture-editing is intended to perform precise visual edits on interior design objects and room scenes. The tool operates in eight distinct editing modes controlled by selectedEditOption. Each mode has a specific purpose, specific required inputs, and specific generation behavior. The AI must strictly follow the active mode's instructions and never blend behaviors across modes.
Temperature: 1 (default Gemini).

EDITING MODES
The active mode is determined by selectedEditOption. Follow ONLY the instructions for the active mode.
${modeSpecificPrompt}

INPUT HIERARCHY
You will receive these inputs from the user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to four:
selectedEditOption (REQUIRED) — determines which editing mode is active. This is the master instruction that defines the entire behavior of the generation. Current mode: '${selectedEditOption}'.
referenceImage1 (REQUIRED) — the primary image upload. Its meaning changes per mode.
referenceImage2 (CONDITIONAL) — the secondary image upload. Only required for certain modes.
${selectedEditOption === 'replacement' ? `referenceImage3 (CONDITIONAL) — the tertiary image upload. Only required for replacement mode — the new object or material to replace with.` : ''}
promptText — include the user's text literally, word for word${promptText ? `: "${promptText}"` : '. Default is empty string.'}.
${selectedEditOption === 'isolate' ? `isolateBackground — only when selectedEditOption = 'isolate'. Current value: '${isolateBackground}'.` : ''}

STRICT RULES:
Follow ONLY the active mode's instructions. Never blend behaviors across modes.
Do not invent, add, or hallucinate any objects, materials, textures, or elements not present in the user's uploaded reference images.
${selectedEditOption === 'material' ? 'In Material Change: the object\'s shape and form are absolute — only the surface material changes. Never alter the object\'s geometry, proportions, or silhouette.' : ''}
${selectedEditOption === 'realism' ? 'In Realism Enhancement: the composition is absolute — never change object positions, room layout, color palette, or lighting direction. Only enhance quality and depth.' : ''}
${selectedEditOption === 'placement' ? 'In Object & Material Placement: the original room scene must be preserved precisely — same camera angle, perspective, lighting, architectural details, existing furniture positions. Only add the placed element. Perspective foreshortening must match the scene\'s camera angle and vanishing points.' : ''}
${selectedEditOption === 'replacement' ? 'In Object & Material Replacement: all elements except the replaced one must remain completely untouched. The replacement must adopt the scene\'s lighting, shadows, and spatial context. Reconstruct surrounding surfaces seamlessly if footprints differ.' : ''}
${selectedEditOption === 'movement' ? 'In Object Movement: all objects except the target must remain completely untouched. Fill vacated space naturally.' : ''}
${selectedEditOption === 'removal' ? 'In Object Removal: all objects except the target must remain completely untouched. Reconstruct hidden surfaces seamlessly.' : ''}
${selectedEditOption === 'texture' ? 'In Texture Extraction: the output must be pure material only — no objects, no room context, no borders. Full-frame texture filling the entire image.' : ''}
${selectedEditOption === 'isolate' ? 'In Isolate on Background: edges must be clean with no background remnants. The object must retain its original visual appearance exactly.' : ''}
If any input is missing or empty, skip it silently without generating filler content.
No explanations, no formatting, no headings in output.

Output dimensions: ${dimensions.width}x${dimensions.height} pixels (${selectedImageSize} aspect ratio)`;
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    // Validate editing mode
    if (!selectedEditOption) {
      toast?.error('Please select an editing mode.');
      isGeneratingRef.current = false;
      return;
    }

    // Validate primary image
    if (!referenceImage1 || referenceImage1?.length === 0) {
      const modeLabels = {
        'material': 'object',
        'realism': 'image to enhance',
        'placement': 'room',
        'replacement': 'room',
        'movement': 'room',
        'removal': 'room',
        'texture': 'image containing the material',
        'isolate': 'object'
      };
      const label = modeLabels[selectedEditOption] || 'reference';
      toast?.error(`Please upload at least one ${label} image.`);
      isGeneratingRef.current = false;
      return;
    }

    // Validate secondary image for modes that require it
    const modesRequiringSecondImage = ['material', 'placement', 'replacement', 'movement', 'removal'];
    if (modesRequiringSecondImage.includes(selectedEditOption) && (!referenceImage2 || referenceImage2?.length === 0)) {
      const modeLabels = {
        'material': 'material',
        'placement': 'object to place',
        'replacement': 'current object to replace',
        'movement': 'object to move',
        'removal': 'object to remove'
      };
      const label = modeLabels[selectedEditOption];
      toast?.error(`Please upload at least one ${label} image.`);
      isGeneratingRef.current = false;
      return;
    }

    // Validate third image for replacement mode
    if (selectedEditOption === 'replacement' && (!referenceImage3 || referenceImage3?.length === 0)) {
      toast?.error('Please upload at least one new object image.');
      isGeneratingRef.current = false;
      return;
    }

    // Validate isolate background if in isolate mode
    if (selectedEditOption === 'isolate' && !isolateBackground) {
      toast?.error('Please select a background option (white, black, or auto).');
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast?.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      toast?.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast?.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setGeneratedImage(null);
    setGenerationError(null);

    // Create abort controller
    abortControllerRef.current = new AbortController();

    try {
      const dimensions = getImageDimensions();
      const toolSystemPrompt = buildSystemPrompt();

      // Route to appropriate service based on selected model
      let generatedImageUrl;

      if (isFluxModel(selectedModel)) {
        const allRefImages = [...referenceImage1, ...referenceImage2, ...referenceImage3];
        generatedImageUrl = await generateWithFlux({
          modelName: selectedModel,
          userPromptText: promptText?.trim() || '',
          referenceImages: allRefImages,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImageUrl = await generateFurnitureEditing({
          selectedEditOption: selectedEditOption,
          referenceImage1: referenceImage1,
          referenceImage2: referenceImage2,
          referenceImage3: referenceImage3,
          promptText: promptText?.trim() || '',
          isolateBackground: isolateBackground || 'auto',
          modelName: selectedModel,
          dimensions: dimensions,
          aspectRatio: selectedImageSize,
          toolSystemPrompt: toolSystemPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      setGeneratedImage(generatedImageUrl);
      setLibraryAssets((prev) => [...prev, generatedImageUrl]);
      setSessionLibraryImages((prev) => [...prev, generatedImageUrl]);
      clearTimer();
      setIsGenerating(false);
      setGenerationError('');
      toast?.success('Image generated successfully!');
      showSaveModal(generatedImageUrl, location.pathname);

      // Save to database
      await saveGenerationToDatabase(generatedImageUrl, promptText || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));

    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        console.error('Error generating image:', error);
        setFailed();
        setGenerationError(error?.message || 'Failed to generate image');
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `Furniture ${selectedEditOption || 'Edit'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'furniture-editing',
          toolName: 'Furniture Editing',
          status: 'completed',
          tags: [selectedEditOption, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      const dimensions = getImageDimensions();
      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: selectedModel,
        imageSize: `${dimensions.width}x${dimensions.height}`,
        style: selectedEditOption || 'Furniture Edit',
        resolution: `${dimensions.width}x${dimensions.height}`,
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...referenceImage1, ...referenceImage2, ...referenceImage3],
        additionalParams: {
          prompt: prompt,
          selectedEditOption: selectedEditOption,
          isolateBackground: isolateBackground,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  const handleDownload = async () => {
    if (!generatedImage) return;
    await downloadImage(generatedImage, `furniture-edit-${selectedEditOption}-${Date.now()}.png`);
  };

  const handleDelete = () => {
    if (!generatedImage) return;
    setGeneratedImage(null);
    setGenerationError(null);
  };

  const handleRegenerate = () => {
    if (!generatedImage) return;
    setGenerationError(null);
    handleGenerate();
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Toaster position="top-center" />
      <Helmet>
        <title>Furniture & Materials Editing - Roomwork.ai</title>
        <meta name="description" content="Perform precise visual edits on interior design objects and room scenes with AI-powered furniture and material editing tools" />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          {/* Narrow Fixed-Width Centered Container */}
          <div className="max-w-[1400px] mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>

              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">Furniture and Materials editing</h1>
                <div className="flex items-center gap-3">
                  <button
                    onClick={openGuide}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
                    <Icon name="HelpCircle" size={18} />
                    <span className="hidden md:inline">Virtual Tool Guide</span>
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline text-sm font-medium">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout - Main Content */}
            <div className="hidden md:flex gap-6">
              {/* Left Column (30%) - Image Editing Options + Upload Windows */}
              <div className="w-[30%] flex-shrink-0 space-y-6">
                {/* Image Editing Options */}
                <div
                  ref={editModeDesktopRef}
                  className="rounded-3xl"
                  style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                <div
                  className="glassmorphic-card rounded-3xl p-6"
                >
                  <h3 className="text-[12.6px] font-medium text-white mb-4">Choose Editing Mode:<span className="text-red-500">*</span></h3>

                  <div className="flex flex-col gap-3">
                    {/* Material Change */}
                    <button
                      onClick={() => handleEditOptionClick('material')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'material' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="Palette" size={18} className="text-orange-500" />
                      <span>Material Change</span>
                    </button>

                    {/* Realism Enhancement */}
                    <button
                      ref={realismButtonRef}
                      onClick={() => handleEditOptionClick('realism')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'realism' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'realism' ? 'animate-highlight-glow' : ''}`
                      }>
                      <Icon name="Sparkles" size={18} className="text-orange-500" />
                      <span>Realism & Quality Enhancement</span>
                    </button>

                    {/* Object & Material Placement */}
                    <button
                      onClick={() => handleEditOptionClick('placement')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'placement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="PackagePlus" size={18} className="text-orange-500" />
                      <span>Object & Material Placement</span>
                    </button>

                    {/* Object Replacement */}
                    <button
                      onClick={() => handleEditOptionClick('replacement')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'replacement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="ArrowLeftRight" size={18} className="text-orange-500" />
                      <span>Object & Material Replacement</span>
                    </button>

                    {/* Object Movement */}
                    <button
                      onClick={() => handleEditOptionClick('movement')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'movement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="Move" size={18} className="text-orange-500" />
                      <span>Object Movement</span>
                    </button>

                    {/* Object Removal */}
                    <button
                      ref={removalButtonRef}
                      onClick={() => handleEditOptionClick('removal')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'removal' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'removal' ? 'animate-highlight-glow' : ''}`
                      }>
                      <Icon name="Eraser" size={18} className="text-orange-500" />
                      <span>Object Removal</span>
                    </button>

                    {/* Texture Extraction */}
                    <button
                      ref={textureButtonRef}
                      onClick={() => handleEditOptionClick('texture')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'texture' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'texture' ? 'animate-highlight-glow' : ''}`
                      }>
                      <Icon name="Layers" size={18} className="text-orange-500" />
                      <span>Texture Extraction</span>
                    </button>

                    {/* Isolate on Background */}
                    <button
                      onClick={() => handleEditOptionClick('isolate')}
                      className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'isolate' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="Frame" size={18} className="text-orange-500" />
                      <span>Isolate on Background</span>
                    </button>
                  </div>

                  {/* Isolate Background Options */}
                  {selectedEditOption === 'isolate' && (
                    <div className="flex gap-3 mt-3">
                      <button
                        onClick={() => handleIsolateBackgroundClick('white')}
                        className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'white' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                        }>
                        <div className="w-4 h-4 rounded-full bg-white border border-white/30" />
                        <span>White</span>
                      </button>
                      <button
                        onClick={() => handleIsolateBackgroundClick('black')}
                        className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'black' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                        }>
                        <div className="w-4 h-4 rounded-full bg-black border border-white/30" />
                        <span>Black</span>
                      </button>
                      <button
                        onClick={() => handleIsolateBackgroundClick('auto')}
                        className={`rounded-2xl px-4 py-3 text-sm text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'auto' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                        }>
                        <Icon name="Wand2" size={16} className="text-orange-500" />
                        <span>Auto</span>
                      </button>
                    </div>
                  )}
                </div>
                </div>{/* end editMode guide wrapper */}

                <div
                  ref={assetLibraryFurnitureDesktopRef}
                  style={{ borderRadius: '1.5rem', boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined }}
                >
                  <AssetLibraryPanel
                    libraryAssets={libraryAssets}
                    setLibraryAssets={setLibraryAssets}
                    generatedImages={sessionLibraryImages}
                    setGeneratedImages={setSessionLibraryImages}
                    currentProjectId={currentProjectId}
                    onImageDeleted={(remaining) => {
                      if (!isGenerating) setGeneratedImage(remaining[0] || null);
                    }}
                  />
                </div>

              </div>

              {/* Right Column (70%) - Generating Images and Image Editing Options */}
              <div className="w-[70%] flex-shrink-0 space-y-6">
                {/* Generating Images Window */}
                <div
                  ref={resultFurnitureDesktopRef}
                  className="rounded-3xl"
                  style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                <div
                  className="glassmorphic-card rounded-3xl p-6"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[12.6px] font-medium text-white">Result</h3>
                    <span className="text-orange-500 text-[12.6px] font-medium"></span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-black/40 border-2 border-dashed border-white/20 h-[400px] flex items-center justify-center">
                    {generatedImage ? (
                      <img
                        src={generatedImage}
                        alt="Generated result"
                        className="w-full h-full object-contain cursor-grab active:cursor-grabbing"
                        draggable
                        onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                        style={{ aspectRatio: selectedImageSize === 'Auto' ? '1/1' : selectedImageSize.replace(':', '/') }}
                      />
                    ) : isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                          <p className="text-white/70 text-sm">Generating your image...</p>
                          <p className="text-white/50 text-xs mt-2">This may take a few moments</p>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                          <p className="text-white/50 text-sm">No image generated yet</p>
                          <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                        </div>
                      </div>
                    )}

                    {/* Standardized icon buttons overlay */}
                    {generatedImage && (
                      <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                        <button
                          onClick={handleDownload}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Download">
                          <Icon name="Download" size={18} className="text-white" />
                        </button>
                        <button
                          onClick={() => {/* Draw functionality */ }}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Draw">
                          <Icon name="Pencil" size={18} className="text-white" />
                        </button>
                        <button
                          onClick={handleDelete}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Delete">
                          <Icon name="Trash2" size={18} className="text-white" />
                        </button>
                        <button
                          onClick={handleRegenerate}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Regenerate">
                          <Icon name="RefreshCw" size={18} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                </div>{/* end result guide wrapper */}

                {/* Upload Images - Dynamic based on editing mode */}
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploads >= 6 ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / 6 images uploaded
                  </span>
                </div>
                <div className="flex gap-6">
                  {/* Upload Window 1 */}
                  <div
                    ref={upload1DesktopRef}
                    className={`${uploadConfig.showSecond ? 'flex-1' : 'w-full'} rounded-3xl`}
                    style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  >
                  <div
                    className={`${uploadConfig.showSecond ? 'flex-1' : 'w-full'} glassmorphic-card rounded-3xl p-6`}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-[12.6px] font-medium text-white flex items-center gap-1.5">
                        {uploadConfig.label1}<span className="text-red-500">*</span>
                        {uploadConfig.tip1 && (
                          <span className="relative group">
                            <Icon name="Info" size={14} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              {uploadConfig.tip1}
                            </span>
                          </span>
                        )}
                      </h3>
                    </div>
                    <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                        disabled={totalUploads >= 6}
                        onChange={handleReferenceImage1Upload} />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-12 flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging1
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                          }`}
                        onDragOver={handleDragOver1}
                        onDragLeave={handleDragLeave1}
                        onDrop={handleDrop1}
                      >
                        <Icon name="ImagePlus" size={48} className="text-orange-500 mb-3" />
                        <p className="text-white/70 text-sm">Drop your image here or click to browse</p>
                        <p className="text-white/50 text-xs mt-1">Supports PNG, JPG</p>
                      </div>
                    </label>
                    {referenceImage1?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {referenceImage1?.map((img, idx) => (
                          <UploadedImageThumb
                            key={idx}
                            src={img}
                            alt={`Upload 1 - ${idx + 1}`}
                            onRemove={() => handleRemoveReferenceImage1(idx)}
                            imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                            height="h-auto"
                            previewWidth="w-[200px]"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  </div>{/* end upload1 guide wrapper */}

                  {/* Upload Window 2 - Conditional */}
                  <div
                    ref={upload2DesktopRef}
                    className="flex-1 rounded-3xl"
                    style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  >
                  {uploadConfig.showSecond && (
                    <div className="w-full glassmorphic-card rounded-3xl p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-[12.6px] font-medium text-white flex items-center gap-1.5">
                          {uploadConfig.label2}<span className="text-red-500">*</span>
                          {uploadConfig.tip2 && (
                            <span className="relative group">
                              <Icon name="Info" size={14} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                {uploadConfig.tip2}
                              </span>
                            </span>
                          )}
                        </h3>
                      </div>
                      <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="file"
                          multiple
                          accept="image/png,image/jpeg,image/jpg"
                          className="hidden"
                          disabled={totalUploads >= 6}
                          onChange={handleReferenceImage2Upload} />
                        <div
                          className={`glassmorphic-upload-zone rounded-2xl p-12 flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging2
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/20 hover:border-orange-500/40'
                            }`}
                          onDragOver={handleDragOver2}
                          onDragLeave={handleDragLeave2}
                          onDrop={handleDrop2}
                        >
                          <Icon name="ImagePlus" size={48} className="text-orange-500 mb-3" />
                          <p className="text-white/70 text-sm">Drop your image here or click to browse</p>
                          <p className="text-white/50 text-xs mt-1">Supports PNG, JPG</p>
                        </div>
                      </label>
                      {referenceImage2?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {referenceImage2?.map((img, idx) => (
                            <UploadedImageThumb
                              key={idx}
                              src={img}
                              alt={`Upload 2 - ${idx + 1}`}
                              onRemove={() => handleRemoveReferenceImage2(idx)}
                              imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                              height="h-auto"
                              previewWidth="w-[200px]"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  </div>{/* end upload2 guide wrapper */}

                  {/* Upload Window 3 - For Replacement mode */}
                  {uploadConfig.showThird && (
                    <div className="flex-1 glassmorphic-card rounded-3xl p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-[12.6px] font-medium text-white flex items-center gap-1.5">
                          {uploadConfig.label3}<span className="text-red-500">*</span>
                          {uploadConfig.tip3 && (
                            <span className="relative group">
                              <Icon name="Info" size={14} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                {uploadConfig.tip3}
                              </span>
                            </span>
                          )}
                        </h3>
                      </div>
                      <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="file"
                          multiple
                          accept="image/png,image/jpeg,image/jpg"
                          className="hidden"
                          disabled={totalUploads >= 6}
                          onChange={handleReferenceImage3Upload} />
                        <div
                          className={`glassmorphic-upload-zone rounded-2xl p-12 flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging3
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/20 hover:border-orange-500/40'
                            }`}
                          onDragOver={handleDragOver3}
                          onDragLeave={handleDragLeave3}
                          onDrop={handleDrop3}
                        >
                          <Icon name="ImagePlus" size={48} className="text-orange-500 mb-3" />
                          <p className="text-white/70 text-sm">Drop your image here or click to browse</p>
                          <p className="text-white/50 text-xs mt-1">Supports PNG, JPG</p>
                        </div>
                      </label>
                      {referenceImage3?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {referenceImage3?.map((img, idx) => (
                            <UploadedImageThumb
                              key={idx}
                              src={img}
                              alt={`Upload 3 - ${idx + 1}`}
                              onRemove={() => handleRemoveReferenceImage3(idx)}
                              imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                              height="h-auto"
                              previewWidth="w-[200px]"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Model Selection, Aspect Ratio, Prompt & Generate */}
                <div className="glassmorphic-card rounded-3xl p-6">
                  {/* First Level: Model Selection & Aspect Ratio */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Model Selection */}
                    <div
                      ref={modelFurnitureDesktopRef}
                      style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined }}
                    >
                      <ModelSelectionDropdown
                        value={selectedModel}
                        onChange={setSelectedModel}
                        excludeFlux
                      />
                    </div>

                    {/* Aspect Ratio Selector */}
                    <div
                      className="relative"
                      ref={(el) => { aspectRatioRef.current = el; }}
                      style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined }}
                    >
                      <label className="text-white/70 text-xs mb-2 flex items-center gap-1.5">Aspect Ratio
                        <span className="relative group">
                          <Icon name="Info" size={14} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-[#1a1a1a] border border-white/10 rounded-lg w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                            Room size ratio must match aspect ratio to maintain original room spacing
                          </span>
                        </span>
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                          className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <AspectRatioIcon ratio={selectedImageSize} size={18} className="text-orange-500" />
                            <span className="text-white text-sm">{selectedImageSize}</span>
                          </div>
                          <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                        </button>
                        {isAspectRatioOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {aspectRatios?.map((ratio) => (
                              <button
                                key={ratio?.value}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${selectedImageSize === ratio?.value
                                    ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                    : 'hover:bg-white/5'
                                  }`}
                              >
                                <div className="flex items-center gap-3">
                                  <AspectRatioIcon
                                    ratio={ratio?.value}
                                    size={18}
                                    className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                                  />
                                  <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {ratio?.label}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resolution Row */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {/* Resolution Selection */}
                    <div
                      className="relative"
                      ref={(el) => { resolutionRef.current = el; }}
                      style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined }}
                    >
                      <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                          className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                        >
                          <span className="text-white text-sm font-medium">{selectedResolution}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs">
                                {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                              </span>
                            <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                          </div>
                        </button>
                        {isResolutionOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {RESOLUTION_OPTIONS.map((resKey) => {
                              const dims = getResolutionDimensions(resKey);
                              return (
                                <button
                                  key={resKey}
                                  type="button"
                                  onClick={() => {
                                    setSelectedResolution(resKey);
                                    setIsResolutionOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${selectedResolution === resKey
                                      ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                      : 'hover:bg-white/5'
                                    }`}
                                >
                                  <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {resKey}
                                  </span>
                                  <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                      {dims.width}×{dims.height}
                                    </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Second Level: Prompt & Generate */}
                  <div className="space-y-4 mt-6">
                    <div
                      ref={promptFurnitureDesktopRef}
                      style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined }}
                    >
                      <label className="block text-white text-[12.6px] font-medium mb-2">Prompt:</label>
                      <textarea
                        ref={promptRef}
                        value={promptText}
                        onChange={(e) => setPromptText(e?.target?.value)}
                        placeholder="Additional request"
                        rows={5}
                        className="w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 border border-white/10 focus:border-orange-500/50 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px]"
                        style={{ height: '120px' }}
                      />
                    </div>

                    <div
                      ref={generateFurnitureDesktopRef}
                      className="rounded-2xl"
                      style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                    >
                      <button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGenerating ? (
                          <>
                            <Icon name="Loader" size={18} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Icon name="Sparkles" size={18} />
                            Generate Image
                          </>
                        )}
                      </button>
                    </div>
                    {isGenerating && (
                      <button
                        onClick={handleCancelGeneration}
                        className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                      >
                        <Icon name="X" size={16} />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-6">
              {/* Image Editing Options - Mobile */}
              <div
                ref={editModeMobileRef}
                className="rounded-3xl"
                style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
              <div
                className="glassmorphic-card rounded-3xl p-5"
              >
                <h3 className="text-white text-xs font-medium mb-4">Choose Editing Mode:<span className="text-red-500">*</span></h3>

                <div className="flex flex-col gap-3">
                  {/* Material Change */}
                  <button
                    onClick={() => handleEditOptionClick('material')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'material' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                    }>
                    <Icon name="Palette" size={16} className="text-orange-500" />
                    <span>Material Change</span>
                  </button>

                  {/* Realism Enhancement */}
                  <button
                    ref={realismButtonMobileRef}
                    onClick={() => handleEditOptionClick('realism')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'realism' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'realism' ? 'animate-highlight-glow' : ''}`
                    }>
                    <Icon name="Sparkles" size={16} className="text-orange-500" />
                    <span>Realism & Quality Enhancement</span>
                  </button>

                  {/* Object & Material Placement */}
                  <button
                    onClick={() => handleEditOptionClick('placement')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'placement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                    }>
                    <Icon name="PackagePlus" size={16} className="text-orange-500" />
                    <span>Object & Material Placement</span>
                  </button>

                  {/* Object Replacement */}
                  <button
                    onClick={() => handleEditOptionClick('replacement')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'replacement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                    }>
                    <Icon name="ArrowLeftRight" size={16} className="text-orange-500" />
                    <span>Object & Material Replacement</span>
                  </button>

                  {/* Object Movement */}
                  <button
                    onClick={() => handleEditOptionClick('movement')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'movement' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                    }>
                    <Icon name="Move" size={16} className="text-orange-500" />
                    <span>Object Movement</span>
                  </button>

                  {/* Object Removal */}
                  <button
                    ref={removalButtonMobileRef}
                    onClick={() => handleEditOptionClick('removal')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'removal' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'removal' ? 'animate-highlight-glow' : ''}`
                    }>
                    <Icon name="Eraser" size={16} className="text-orange-500" />
                    <span>Object Removal</span>
                  </button>

                  {/* Texture Extraction */}
                  <button
                    ref={textureButtonMobileRef}
                    onClick={() => handleEditOptionClick('texture')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'texture' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'} ${highlightButton === 'texture' ? 'animate-highlight-glow' : ''}`
                    }>
                    <Icon name="Layers" size={16} className="text-orange-500" />
                    <span>Texture Extraction</span>
                  </button>

                  {/* Isolate on Background */}
                  <button
                    onClick={() => handleEditOptionClick('isolate')}
                    className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start ${selectedEditOption === 'isolate' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                    }>
                    <Icon name="Frame" size={16} className="text-orange-500" />
                    <span>Isolate on Background</span>
                  </button>
                </div>

                {/* Isolate Background Options - Mobile */}
                {selectedEditOption === 'isolate' && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleIsolateBackgroundClick('white')}
                      className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'white' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <div className="w-3 h-3 rounded-full bg-white border border-white/30" />
                      <span>White</span>
                    </button>
                    <button
                      onClick={() => handleIsolateBackgroundClick('black')}
                      className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'black' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <div className="w-3 h-3 rounded-full bg-black border border-white/30" />
                      <span>Black</span>
                    </button>
                    <button
                      onClick={() => handleIsolateBackgroundClick('auto')}
                      className={`rounded-2xl px-3 py-3 text-xs text-white transition-all flex items-center gap-2 justify-start flex-1 ${isolateBackground === 'auto' ? 'bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]' : 'bg-white/5 border border-white/10 hover:border-orange-500/30'}`
                      }>
                      <Icon name="Wand2" size={14} className="text-orange-500" />
                      <span>Auto</span>
                    </button>
                  </div>
                )}
              </div>
              </div>{/* end editMode mobile guide wrapper */}

              {/* Result Window - Mobile */}
              <div
                ref={resultFurnitureMobileRef}
                className="rounded-3xl"
                style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
              <div
                className="glassmorphic-card rounded-3xl p-5"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-white text-xs font-medium">Result</h3>
                </div>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 border-2 border-dashed border-white/20 min-h-[250px] flex items-center justify-center">
                  {generatedImage ? (
                    <img
                      src={generatedImage}
                      alt="Generated result"
                      className="max-w-full max-h-full cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                      style={{ aspectRatio: selectedImageSize === 'Auto' ? '1/1' : selectedImageSize.replace(':', '/') }}
                    />
                  ) : isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                        <p className="text-white/70 text-sm">Generating your image...</p>
                        <p className="text-white/50 text-xs mt-2">This may take a few moments</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                        <p className="text-white/50 text-xs">No image generated yet</p>
                        <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </div>{/* end result mobile guide wrapper */}

              <div
                ref={assetLibraryFurnitureMobileRef}
                style={{ borderRadius: '1.5rem', boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined }}
              >
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setGeneratedImage(remaining[0] || null);
                  }}
                />
              </div>

              {/* Upload Windows - Dynamic based on editing mode */}
              <div className="text-left mb-2">
                <span className={`text-sm font-medium ${totalUploads >= 6 ? "text-red-400" : "text-white/70"}`}>
                  {totalUploads} / 6 images uploaded
                </span>
              </div>
              <div className="flex gap-3">
                {/* Upload Window 1 */}
                <div
                  ref={upload1MobileRef}
                  className={`${uploadConfig.showSecond ? 'flex-1' : 'w-full'} rounded-3xl`}
                  style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                <div
                  className={`${uploadConfig.showSecond ? 'flex-1' : 'w-full'} glassmorphic-card rounded-3xl p-4`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-white text-xs font-medium flex items-center gap-1">
                      {uploadConfig.label1}<span className="text-red-500">*</span>
                      {uploadConfig.tip1 && (
                        <span className="relative group">
                          <Icon name="Info" size={12} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                            {uploadConfig.tip1}
                          </span>
                        </span>
                      )}
                    </h3>
                  </div>
                  <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      disabled={totalUploads >= 6}
                      onChange={handleReferenceImage1Upload} />
                    <div
                      className={`glassmorphic-upload-zone rounded-2xl h-[120px] flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging1
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                        }`}
                      onDragOver={handleDragOver1}
                      onDragLeave={handleDragLeave1}
                      onDrop={handleDrop1}
                    >
                      <Icon name="ImagePlus" size={32} className="text-orange-500 mb-2" />
                      <p className="text-white text-xs">Drop or Upload</p>
                    </div>
                  </label>
                  {referenceImage1?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {referenceImage1?.map((img, idx) => (
                        <UploadedImageThumb
                          key={idx}
                          src={img}
                          alt={`Upload 1 - ${idx + 1}`}
                          onRemove={() => handleRemoveReferenceImage1(idx)}
                          imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                          height="h-auto"
                          previewWidth="w-[200px]"
                        />
                      ))}
                    </div>
                  )}
                </div>
                </div>{/* end upload1 mobile guide wrapper */}

                {/* Upload Window 2 - Conditional */}
                <div
                  ref={upload2MobileRef}
                  className="flex-1 rounded-3xl"
                  style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                {uploadConfig.showSecond && (
                  <div className="w-full glassmorphic-card rounded-3xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-white text-xs font-medium flex items-center gap-1">
                        {uploadConfig.label2}<span className="text-red-500">*</span>
                        {uploadConfig.tip2 && (
                          <span className="relative group">
                            <Icon name="Info" size={12} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              {uploadConfig.tip2}
                            </span>
                          </span>
                        )}
                      </h3>
                    </div>
                    <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                        disabled={totalUploads >= 6}
                        onChange={handleReferenceImage2Upload} />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl h-[120px] flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging2
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                          }`}
                        onDragOver={handleDragOver2}
                        onDragLeave={handleDragLeave2}
                        onDrop={handleDrop2}
                      >
                        <Icon name="ImagePlus" size={32} className="text-orange-500 mb-2" />
                        <p className="text-white text-xs">Drop or Upload</p>
                      </div>
                    </label>
                    {referenceImage2?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {referenceImage2?.map((img, idx) => (
                          <UploadedImageThumb
                            key={idx}
                            src={img}
                            alt={`Upload 2 - ${idx + 1}`}
                            onRemove={() => handleRemoveReferenceImage2(idx)}
                            imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                            height="h-auto"
                            previewWidth="w-[200px]"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </div>{/* end upload2 mobile guide wrapper */}

                {/* Upload Window 3 - For Replacement mode (Mobile) */}
                {uploadConfig.showThird && (
                  <div className="flex-1 glassmorphic-card rounded-3xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-white text-xs font-medium flex items-center gap-1">
                        {uploadConfig.label3}<span className="text-red-500">*</span>
                        {uploadConfig.tip3 && (
                          <span className="relative group">
                            <Icon name="Info" size={12} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] text-white bg-[#1a1a1a] border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              {uploadConfig.tip3}
                            </span>
                          </span>
                        )}
                      </h3>
                    </div>
                    <label className={`block ${totalUploads >= 6 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                        disabled={totalUploads >= 6}
                        onChange={handleReferenceImage3Upload} />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl h-[120px] flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${isDragging3
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                          }`}
                        onDragOver={handleDragOver3}
                        onDragLeave={handleDragLeave3}
                        onDrop={handleDrop3}
                      >
                        <Icon name="ImagePlus" size={32} className="text-orange-500 mb-2" />
                        <p className="text-white text-xs">Drop or Upload</p>
                      </div>
                    </label>
                    {referenceImage3?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {referenceImage3?.map((img, idx) => (
                          <UploadedImageThumb
                            key={idx}
                            src={img}
                            alt={`Upload 3 - ${idx + 1}`}
                            onRemove={() => handleRemoveReferenceImage3(idx)}
                            imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                            height="h-auto"
                            previewWidth="w-[200px]"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Model Selection, Aspect Ratio, Prompt & Generate - Mobile */}
              <div className="glassmorphic-card rounded-3xl p-5">
                {/* First Level: Model Selection & Aspect Ratio */}
                <div className="space-y-3 mb-4">
                  {/* Model Selection */}
                  <div
                    ref={modelFurnitureMobileRef}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined }}
                  >
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                      mobile
                      excludeFlux
                    />
                  </div>

                  {/* Aspect Ratio Selector */}
                  <div
                    className="relative"
                    ref={(el) => { aspectRatioMobileRef.current = el; }}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined }}
                  >
                    <label className="text-white/70 text-xs mb-2 flex items-center gap-1.5">Aspect Ratio
                      <span className="relative group">
                        <Icon name="Info" size={12} className="text-white/40 hover:text-orange-500 transition-colors cursor-help" />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] text-white bg-[#1a1a1a] border border-white/10 rounded-lg w-44 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          Room size ratio must match aspect ratio to maintain original room spacing
                        </span>
                      </span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                        className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <AspectRatioIcon ratio={selectedImageSize} size={18} className="text-orange-500" />
                          <span className="text-white text-sm">{selectedImageSize}</span>
                        </div>
                        <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                      </button>
                      {isAspectRatioOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {aspectRatios?.map((ratio) => (
                            <button
                              key={ratio?.value}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                              className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${selectedImageSize === ratio?.value
                                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                  : 'hover:bg-white/5'
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                <AspectRatioIcon
                                  ratio={ratio?.value}
                                  size={18}
                                  className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                                />
                                <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {ratio?.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resolution Selection - Mobile */}
                  <div
                    className="relative col-span-2"
                    ref={(el) => { resolutionMobileRef.current = el; }}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined }}
                  >
                    <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                        className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                      >
                        <span className="text-white text-sm font-medium">{selectedResolution}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-xs">
                              {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                            </span>
                          <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                        </div>
                      </button>
                      {isResolutionOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {RESOLUTION_OPTIONS.map((resKey) => {
                            const dims = getResolutionDimensions(resKey);
                            return (
                              <button
                                key={resKey}
                                type="button"
                                onClick={() => {
                                  setSelectedResolution(resKey);
                                  setIsResolutionOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${selectedResolution === resKey
                                    ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                    : 'hover:bg-white/5'
                                  }`}
                              >
                                <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {resKey}
                                </span>
                                <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                    {dims.width}×{dims.height}
                                  </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Second Level: Prompt & Generate */}
                <div className="space-y-4 mt-6">
                  <div
                    ref={promptFurnitureMobileRef}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined }}
                  >
                    <label className="block text-white text-xs font-medium mb-2">Prompt:</label>
                    <textarea
                      ref={promptRef}
                      value={promptText}
                      onChange={(e) => setPromptText(e?.target?.value)}
                      placeholder="Additional request"
                      rows={5}
                      className="w-full glassmorphic-input rounded-2xl px-4 py-3 text-sm text-white bg-white/5 border border-white/10 focus:border-orange-500/50 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px]"
                      style={{ height: '120px' }}
                    />
                  </div>

                  <div
                    ref={generateFurnitureMobileRef}
                    className="rounded-xl"
                    style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  >
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full glassmorphic-button rounded-xl py-2.5 text-sm text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <Icon name="Loader" size={16} className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Icon name="Sparkles" size={16} />
                          Generate Image
                        </>
                      )}
                    </button>
                  </div>
                  {isGenerating && (
                    <button
                      onClick={handleCancelGeneration}
                      className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                    >
                      <Icon name="X" size={16} />
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={closeGuide}>
            <div
              className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
            />
          </div>

          {/* Tooltip bubble */}
          <div
            className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const desktopRefsList = [editModeDesktopRef.current, assetLibraryFurnitureDesktopRef.current, upload1DesktopRef.current, upload2DesktopRef.current, modelFurnitureDesktopRef.current, aspectRatioRef.current, resolutionRef.current, promptFurnitureDesktopRef.current, generateFurnitureDesktopRef.current, resultFurnitureDesktopRef.current];
              const mobileRefsList = [editModeMobileRef.current, assetLibraryFurnitureMobileRef.current, upload1MobileRef.current, upload2MobileRef.current, modelFurnitureMobileRef.current, aspectRatioMobileRef.current, resolutionMobileRef.current, promptFurnitureMobileRef.current, generateFurnitureMobileRef.current, resultFurnitureMobileRef.current];
              const isDesktop = window.innerWidth >= 768;
              const ref = isDesktop ? desktopRefsList[guideStep - 1] : mobileRefsList[guideStep - 1];
              const bubbleWidth = isDesktop ? 300 : 280;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              if (!isDesktop) {
                return {
                  top: rect.bottom + 12,
                  left: Math.max(12, Math.min(window.innerWidth - bubbleWidth - 12, rect.left + rect.width / 2 - bubbleWidth / 2)),
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0px)' : 'translateY(-8px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Steps 1-2 are in left column → bubble to the RIGHT
              if (guideStep <= 2) {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.right + 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Steps 3-9 are in right column → bubble to the LEFT
              return {
                top: rect.top + rect.height / 2,
                left: rect.left - bubbleWidth - 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
              {/* Arrow indicator */}
              {window.innerWidth >= 768 && guideStep <= 2 ? (
                <>
                  <div
                    className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderRight: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderRight: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : window.innerWidth >= 768 ? (
                <>
                  <div
                    className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: '9px solid rgba(30, 30, 30, 0.95)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : (
                <>
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderBottom: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              )}

              <div
                className="rounded-2xl p-5 text-center"
                style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 mb-2"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s' }}
                >
                  <Icon
                    name={guideStep === 1 ? 'Wand2' : guideStep === 2 ? 'FolderOpen' : guideStep === 3 ? 'ImagePlus' : guideStep === 4 ? 'Images' : guideStep === 5 ? 'Cpu' : guideStep === 6 ? 'RectangleHorizontal' : guideStep === 7 ? 'ScanLine' : guideStep === 8 ? 'MessageSquare' : guideStep === 9 ? 'Sparkles' : 'Monitor'}
                    size={18}
                    className="text-white/50"
                  />
                  <span className="text-sm font-semibold text-white">Step {guideStep}</span>
                </div>
                <p
                  className="text-sm text-white/80 leading-relaxed"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s' }}
                >
                  {guideStep === 1 ? (
                    <>Choose the <span className="text-white font-medium italic">editing mode</span> that matches your task. Each mode performs a different type of AI-powered furniture or material edit — select the one you need before uploading images.</>
                  ) : guideStep === 2 ? (
                    <>Your saved and generated images are stored in the <span className="text-white font-medium italic">Asset Library</span>. Drag and drop them into upload zones to reuse them as references.</>
                  ) : guideStep === 3 ? (
                    <>Upload your <span className="text-white font-medium italic">primary reference image</span> here. Different modes require different objects or materials to be uploaded.</>
                  ) : guideStep === 4 ? (
                    <>This <span className="text-white font-medium italic">second upload window</span> is not always required — it appears only for modes that need an additional reference, such as a material sample or target object. If your mode doesn't need it, this area won't be shown.</>
                  ) : guideStep === 5 ? (
                    <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                  ) : guideStep === 6 ? (
                    <>Image ratio: vertical or horizontal. Different <span className="text-white font-medium italic">ratios</span> allow you to explore different compositions.</>
                  ) : guideStep === 7 ? (
                    <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                  ) : guideStep === 8 ? (
                    <>Add optional <span className="text-white font-medium italic">text instructions</span> to refine the edit. Describe what material, color, or style change you want applied in addition to the visual references.</>
                  ) : guideStep === 9 ? (
                    <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you've selected a mode and uploaded the required images. The AI will apply your chosen editing mode to the references.</>
                  ) : (
                    <>The AI-generated result will appear in this <span className="text-white font-medium italic">Result</span> window. Once generated, use the buttons to download, save to a project, or regenerate with the same settings.</>
                  )}
                </p>
                <button
                  onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                  className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s' }}
                >
                  {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FurnitureEditing;